import React, { useState, useRef, useEffect } from "react";
import { AxiosError } from "axios";
import moment from "moment-timezone";
import { Constants, COURSE_CATEGORY_OPTIONS, getCourseCategoryValue } from "@eagerdog/constants";
import { IUser, IAddress, ISession, ICourseSummary, ICourse, IClub, DOG_VACCINE_TYPES, DogVaccineType } from "@eagerdog/interfaces";
import { Editor } from '@tinymce/tinymce-react';
import { Editor as TinyMCEEditor } from 'tinymce';
import { TINYMCE_API_KEY, TINYMCE_INIT, TINYMCE_SETUP } from 'src/utils/tinymce';

import { apiService } from "src/services";
import { helperService } from "src/services/helper.service";

import { toast } from "src/components/Toast/ToastManager";
import Input from "src/components/Input/Input";
import Dropdown, { IOption } from "src/components/Dropdown/Dropdown";
import DatePick from "src/components/DatePick/DatePick";
import dateIcon from "src/assets/icons/date.svg";
import UserAddress from "src/routes/EventRegister/UserAddress/UserAddress";
import CourseSchedule from "./CourseSchedule/CourseSchedule";
import { registrationBlockMessage, weeklyScheduleZone } from "./CourseSchedule/weeklySchedule";
import Checkbox from "src/components/Checkbox/Checkbox";

import StepForm, { Step, useStepForm } from "src/components/StepForm/StepForm";

import styles from "./CreateCourse.module.scss";

export const COURSE_VISIBILITY_STEP = 9;

interface IProps {
    course?: ICourseSummary | null;
    clubId?: string;
    initialTab?: number;
    onSubmit(course?: ICourse): void;
}

const CreateCourse: React.FC<IProps> = (props) => {
    const { activeTab, switchToTab } = useStepForm(props.initialTab ?? 0);
    const user:IUser = helperService.getUser();
    const club:IClub = helperService.getClub();

    const [title, setTitle] = useState<string>(props.course?.title || "");

    const description:string = props.course?.description || "";
    const descRef = useRef<TinyMCEEditor | null>(null);

    const [address, setAddress] = useState<IAddress | undefined>(props.course?.address);

    const registrationMessage:string = props.course?.registration_message || "";
    const regMsgRef = useRef<TinyMCEEditor | null>(null);

    const waiver: string = props.course?.waiver || "";
    const waiverRef = useRef<TinyMCEEditor | null>(null);

    const clubPolicies: string = props.course?.club_policies || "";
    const clubPoliciesRef = useRef<TinyMCEEditor | null>(null);

    //const [waitlistSize, setWaitlistSize] = useState<string>(props.course?.waitlist_size?.toString() || "");
    const [price, setPrice] = useState<string>(props.course?.fee?.toString() || "");

    const [currency, setCurrency] = useState<IOption>(props.course?.currency ? { value: props.course.currency.toUpperCase(), id: props.course.currency.toUpperCase() } : { value: "USD", id: "USD" });

    const [visibilitySeed] = useState(() => {
        const publishAt = props.course?.publish_at ? new Date(props.course.publish_at) : undefined;
        const publishAtIsFuture = !!publishAt && publishAt.getTime() > Date.now();
        const timezone = props.course?.timezone;
        return {
            visibility: (!props.course || props.course.status === Constants.course_status.draft)
                ? { value: "Draft", id: "Draft" }
                : { value: "Published", id: "Published" },
            publishOption: publishAtIsFuture
                ? { value: "On a Specific Date", id: "On a Specific Date" }
                : { value: "Immediately", id: "Immediately" },
            publishDate: publishAt
                ? (timezone ? helperService.getDateTimeWithTimezone(timezone, publishAt) : publishAt)
                : new Date(Date.now() + 3600 * 1000 * 24),
        };
    });
    const [courseVisibility, setCourseVisibility] = useState<IOption>(visibilitySeed.visibility);
    const [publishOption, setPublishOption] = useState<IOption>(visibilitySeed.publishOption);
    const [publishDate, setPublishDate] = useState<Date>(visibilitySeed.publishDate);
    const courseTimezone = props.course?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const courseTimezoneLabel = props.course?.timezone
        ? helperService.getTimezoneLabel(props.course.timezone)
        : courseTimezone;

    // Auto-select payment structure based on course type
    const getInitialTotalCourse = (): boolean => {
        if (props.course) {
            // For existing courses, check if sessions have fees
            return !(props.course.schedule && props.course.schedule.length > 0 && props.course.schedule[0].fee !== undefined);
        }
        // For new courses, only full_course defaults to total course pricing
        return true; // Will be overridden by useEffect for drop-in and seminar
    };

    const getInitialPerSession = (): boolean => {
        if (props.course) {
            // For existing courses, check if sessions have fees
            return props.course.schedule && props.course.schedule.length > 0 && props.course.schedule[0].fee !== undefined;
        }
        // For new courses, drop-in and seminar default to per session pricing
        return false; // Will be overridden by useEffect for drop-in and seminar
    };

    const [totalCourse, setTotalCourse] = useState<boolean>(getInitialTotalCourse());
    const [perSession, setPerSession] = useState<boolean>(getInitialPerSession());

    // Course type dropdown options
    const courseTypeOptions: IOption[] = [
        { value: "Course (Full Registration)", id: Constants.course_type.full_course },
        { value: "Class (Drop-In)", id: Constants.course_type.drop_in },
        { value: "Seminar", id: Constants.course_type.seminar }
    ];

    // Initialize course type based on existing course or default to full_course
    const getInitialCourseType = (): IOption => {
        if (props.course?.course_type === Constants.course_type.drop_in) {
            return courseTypeOptions[1];
        } else if (props.course?.course_type === Constants.course_type.seminar) {
            return courseTypeOptions[2];
        }
        return courseTypeOptions[0]; // default to full_course
    };

    const [selectedCourseType, setSelectedCourseType] = useState<IOption>(getInitialCourseType());
    const [entryLimit, setEntryLimit] = useState<number>(
        typeof props.course?.waitlist_limit === "number"
            ? props.course.waitlist_limit
            : (props.course?.schedule?.[0]?.regular_capacity ?? 5)
    );
    const [courseWaitlistSize, setCourseWaitlistSize] = useState<number>(props.course?.waitlist_size || 0);
    const [selectedCategory, setSelectedCategory] = useState<IOption>(
        props.course?.category
            ? { value: COURSE_CATEGORY_OPTIONS.find(l => getCourseCategoryValue(l) === props.course!.category) ?? props.course.category, id: props.course.category }
            : { value: "", id: "" }
    );

    // Keep legacy courseType boolean for backward compatibility with drop-in checkbox if needed
    const [courseType, setCourseType] = useState<boolean>(props.course?.course_type === Constants.course_type.drop_in ? true : false);

    // Auto-select payment structure based on course type
    useEffect(() => {
        if (selectedCourseType.id === Constants.course_type.drop_in || selectedCourseType.id === Constants.course_type.seminar) {
            // Classes and seminars always use per-session pricing
            setTotalCourse(false);
            setPerSession(true);
        } else if (selectedCourseType.id === Constants.course_type.full_course) {
            // Full courses default to total course pricing
            setTotalCourse(true);
            setPerSession(false);
        }
    }, [selectedCourseType]);

    const hydrateSessions = (): ISession[] => {
        let sessions: ISession[] = [];

        for (let s in props.course?.schedule || []) {
            sessions.push(props.course?.schedule ? {
                ...props.course.schedule[s],
                start_date: moment(props.course.schedule[s].start_date).toDate(),
                end_date: moment(props.course.schedule[s].end_date).toDate(),
                regular_capacity: props.course.schedule[s].regular_capacity,
                dropin_capacity: props.course.schedule[s].dropin_capacity
            } : {
                 _id: "",
                 instructors: [],
                 start_date: new Date(),
                 end_date: new Date() ,
                 regular_capacity: 5,
                 dropin_capacity: 0
            });
        }

        return sessions;
    };

    const [sessions, setSessions] = useState<ISession[]>(props.course?.schedule ? hydrateSessions() : []);
    //const [sessions, setSessions] = useState<ISession[]>([]);

    const currencyOptions:IOption[] = [
        { value: "USD", id: "USD" },
        { value: "CAD", id: "CAD" }
    ];

    // ── Vaccine requirements ───────────────────────────────────────────────────
    const existingVaxReqs = props.course?.vaccine_requirements;
    const [vaccineEnabled, setVaccineEnabled] = useState<boolean>(existingVaxReqs?.enabled ?? false);
    const [vaccineTypes, setVaccineTypes] = useState<DogVaccineType[]>(
        (existingVaxReqs?.required_types as DogVaccineType[]) ?? []
    );
    const [vaccineEnforcement, setVaccineEnforcement] = useState<"block" | "warn">(
        existingVaxReqs?.enforcement ?? "warn"
    );
    const [vaccineNotes, setVaccineNotes] = useState<string>(existingVaxReqs?.notes ?? "");

    const toggleVaccineType = (type: DogVaccineType) => {
        setVaccineTypes(prev =>
            prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
        );
    };

    const steps:string[] = [
        "Type",
        "Title",
        "Location",
        "Schedule",
        "Message",
        "Payment Details",
        "Waiver",
        "Club Policies",
        "Vaccine Requirements",
        "Visibility"
    ];

    const throwError = (msg: string) => {
        toast.show({
            title: "Create/Edit Course",
            content: msg,
            duration: 10000,
            type: "fail"
        });
    }

    const isValidCourse = (): boolean => {
        if (title === "") {
            throwError("Title is required");
            switchToTab(1); // Title is now step 1 (was 0)
            return false;
        }

        if (descRef.current && descRef.current.getContent() === '') {
            throwError("Description is required");
            switchToTab(1); // Title is now step 1 (was 0)
            return false;
        }

        if (address?.line1 === "" || address?.city === "" || address?.state === "" || address?.country === "" || address?.postal_code === "") {
            throwError("Location (Line 1, City, State, Country, Postal/Zip Code) is required");
            switchToTab(2); // Location is now step 2 (was 1)
            return false;
        }

        if (sessions.filter((session) => session.instructors.filter((instructor) => instructor.first_name !== "" && instructor.last_name !== "").length > 0).length !== sessions.length) {
            throwError("All sessions must have an instructor with a first and last name");
            switchToTab(3); // Schedule is now step 3 (was 2)
            return false;
        }

        const keptIds = new Set(sessions.map((session) => session._id).filter(Boolean).map(String));
        const dropped = (props.course?.schedule || []).filter((session) => {
            const entries = (session.regular_entries || 0) + (session.dropin_entries || 0);
            return entries > 0 && session._id && !keptIds.has(String(session._id));
        });
        if (dropped.length > 0) {
            throwError(registrationBlockMessage(dropped.map((session) => new Date(session.start_date)), weeklyScheduleZone(props.course?.timezone)));
            switchToTab(3);
            return false;
        }

        return true;
    }

    const visibilityUntouched = () => {
        return courseVisibility.value === visibilitySeed.visibility.value
            && publishOption.value === visibilitySeed.publishOption.value
            && (
                publishOption.value !== "On a Specific Date"
                || publishDate.getTime() === visibilitySeed.publishDate.getTime()
            );
    }

    const courseVisibilityStatus = () => {
        const locked = props.course && (
            props.course.status === Constants.course_status.in_progress
            || props.course.status === Constants.course_status.complete
        );
        if (locked && visibilityUntouched()) {
            return props.course!.status;
        }
        if (courseVisibility.value === "Draft") {
            return Constants.course_status.draft;
        }
        if (publishOption.value === "Immediately") {
            return Constants.course_status.published;
        }
        return Constants.course_status.upcoming;
    }

    const coursePublishAt = (): Date | string | null | undefined => {
        const locked = props.course && (
            props.course.status === Constants.course_status.in_progress
            || props.course.status === Constants.course_status.complete
        );
        if (locked && visibilityUntouched()) {
            return props.course?.publish_at;
        }
        if (courseVisibility.value === "Draft") {
            return null;
        }
        if (publishOption.value === "Immediately") {
            return new Date();
        }
        return moment.tz(moment(publishDate).format("YYYY-MM-DD HH:mm"), courseTimezone).toISOString();
    }

    const createCourse = async () => {
        if (isValidCourse()) {
            const sanitizedSessions = sessions.map((s: ISession) => {
                const session: any = {
                    ...s,
                    fee: perSession ? Number(price) : undefined,
                    instructors: s.instructors.map((instr: any) => {
                        const i = { ...instr };

                        if (i._id === "") {
                            delete i._id;
                        }

                        return i;
                    })
                };

                if (session._id === "") {
                    delete session._id;
                }

                return session;
            });

            const courseData:any = {
                title: title,
                description: descRef.current ? descRef.current.getContent() : '',
                registration_message: regMsgRef.current ? regMsgRef.current.getContent() : '',
                waiver: waiverRef.current ? waiverRef.current.getContent() : '',
                club_policies: clubPoliciesRef.current ? clubPoliciesRef.current.getContent() : '',
                conditions: "Indoor",
                course_type: selectedCourseType.id, // Use the selected course type from dropdown
                category: selectedCategory?.id || undefined,
                address: {
                    line1: address?.line1 || "",
                    line2: address?.line2 || "",
                    city: address?.city || "",
                    state: address?.state || "",
                    country: address?.country || "",
                    postal_code: address?.postal_code || "",
                },
                status: courseVisibilityStatus(),
                publish_at: coursePublishAt(),
                schedule: sanitizedSessions,
                fee: Number(price),
                use_early_bird_fee: false,
                waitlist_limit: selectedCourseType.id === Constants.course_type.drop_in ? undefined : entryLimit,
                waitlist_size: selectedCourseType.id === Constants.course_type.drop_in ? undefined : courseWaitlistSize,
                vaccine_requirements: vaccineEnabled ? {
                    enabled: true,
                    required_types: vaccineTypes,
                    enforcement: vaccineEnforcement,
                    notes: vaccineNotes || undefined,
                } : { enabled: false, required_types: [], enforcement: vaccineEnforcement },
            };

            if (club && club._id) {
                courseData.club_id = club._id;
            } else if (props.clubId) {
                courseData.club_id = props.clubId;
            }

            if (props.course) {
                try {
                    await apiService.updateCourse(props.course._id, courseData);

                    toast.show({
                        title: "Update Course",
                        content: "Course updated successfully",
                        duration: 10000,
                        type: "success"
                    });

                    props.onSubmit();
                } catch (error) {
                    toast.show({
                        title: "Update Course",
                        content: "Failed to update course.",
                        duration: 10000,
                        errorDetails: error as AxiosError,
                        type: "fail"
                    });
                }
            } else {
                try {
                    let course = await apiService.createCourse(courseData);

                    toast.show({
                        title: "Create Course",
                        content: "Course created successfully",
                        duration: 10000,
                        type: "success"
                    });

                    props.onSubmit(course);
                } catch (error) {
                    toast.show({
                        title: "Create Course",
                        content: "Failed to create course.",
                        duration: 10000,
                        errorDetails: error as AxiosError,
                        type: "fail"
                    });
                }
            }
        }
    };

    return (
        <div className={styles.CreateCourse}>
            <div className={styles.title}>{props.course ? "Edit Course" : "Create Course"}</div>
            <div className={styles.formWrap}>
                <StepForm activeTab={activeTab} next={(e) => { switchToTab(e); }} steps={steps} onSubmit={() => { createCourse(); }} finishText={props.course ? "Update Course" : "Create Course"}>
                <Step>
                    <div className={styles.stepTitle}>Course Type</div>
                    <p>What type of course/class is this?</p>
                    
                    <div style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {/* Course (Full Registration) Option */}
                        <div 
                            onClick={() => {
                                setSelectedCourseType(courseTypeOptions[0]);
                                setCourseType(false);
                            }}
                            style={{
                                padding: '15px',
                                border: selectedCourseType.id === Constants.course_type.full_course ? '2px solid #E67E22' : '2px solid #ddd',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                backgroundColor: selectedCourseType.id === Constants.course_type.full_course ? '#FEF0E6' : 'white',
                                transition: 'all 0.2s'
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                                <input
                                    type="radio"
                                    checked={selectedCourseType.id === Constants.course_type.full_course}
                                    onChange={() => {}}
                                    style={{ marginTop: '3px', cursor: 'pointer' }}
                                />
                                <div>
                                    <h3 style={{ margin: '0 0 6px 0', fontSize: '16px', fontWeight: '600', color: '#333' }}>
                                        Course (Full Registration)
                                    </h3>
                                    <p style={{ margin: 0, fontSize: '13px', color: '#666', lineHeight: '1.4' }}>
                                        Participants register for the entire course at once. All sessions are included in a single registration. 
                                        Best for structured training programs where participants attend all sessions.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Class (Drop-In) Option */}
                        <div 
                            onClick={() => {
                                setSelectedCourseType(courseTypeOptions[1]);
                                setCourseType(true);
                            }}
                            style={{
                                padding: '15px',
                                border: selectedCourseType.id === Constants.course_type.drop_in ? '2px solid #E67E22' : '2px solid #ddd',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                backgroundColor: selectedCourseType.id === Constants.course_type.drop_in ? '#FEF0E6' : 'white',
                                transition: 'all 0.2s'
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                                <input
                                    type="radio"
                                    checked={selectedCourseType.id === Constants.course_type.drop_in}
                                    onChange={() => {}}
                                    style={{ marginTop: '3px', cursor: 'pointer' }}
                                />
                                <div>
                                    <h3 style={{ margin: '0 0 6px 0', fontSize: '16px', fontWeight: '600', color: '#333' }}>
                                        Class (Drop-In)
                                    </h3>
                                    <p style={{ margin: 0, fontSize: '13px', color: '#666', lineHeight: '1.4' }}>
                                        Participants can register for individual sessions. Great for flexible scheduling where people 
                                        can choose which specific dates work for them and their dogs.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Seminar Option */}
                        <div 
                            onClick={() => {
                                setSelectedCourseType(courseTypeOptions[2]);
                                setCourseType(false);
                            }}
                            style={{
                                padding: '15px',
                                border: selectedCourseType.id === Constants.course_type.seminar ? '2px solid #E67E22' : '2px solid #ddd',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                backgroundColor: selectedCourseType.id === Constants.course_type.seminar ? '#FEF0E6' : 'white',
                                transition: 'all 0.2s'
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                                <input
                                    type="radio"
                                    checked={selectedCourseType.id === Constants.course_type.seminar}
                                    onChange={() => {}}
                                    style={{ marginTop: '3px', cursor: 'pointer' }}
                                />
                                <div>
                                    <h3 style={{ margin: '0 0 6px 0', fontSize: '16px', fontWeight: '600', color: '#333' }}>
                                        Seminar
                                    </h3>
                                    <p style={{ margin: 0, fontSize: '13px', color: '#666', lineHeight: '1.4' }}>
                                        Educational event for people only (no dogs). Participants register themselves for workshops, 
                                        lectures, or training sessions. Perfect for handler education, judge seminars, or theory classes.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div style={{ marginTop: '28px' }}>
                        <div className={styles.stepTitle}>Category</div>
                        <p>What discipline does this course or class cover?</p>
                        <Dropdown
                            label="Select a category"
                            placeholder="Select a category…"
                            options={COURSE_CATEGORY_OPTIONS.map(label => ({ value: label, id: getCourseCategoryValue(label) }))}
                            value={selectedCategory}
                            onChange={(_e: any, option: IOption) => setSelectedCategory(option)}
                        />
                    </div>
                </Step>
                <Step>
                    <div className={styles.stepTitle}>Title &amp; Description</div>
                    <p>What's the title of your course/class?</p>
                    <Input required label="Title" placeholder="Whats the title of the class/course?" value={title} onChange={(e) => setTitle(e.target.value)} />

                    <p style={{ marginTop: '20px' }}>What is the description of your course/class?</p>
                    <Editor 
                                apiKey={TINYMCE_API_KEY}
                                onInit={(evt, editor) => descRef.current = editor}
                                init={{ ...TINYMCE_INIT, setup: TINYMCE_SETUP }}
                                initialValue={description}
                            />
                </Step>
                <Step>
                    <div className={styles.stepTitle}>Location</div>
                    <p>Where is the location of your course/class?</p>
                    <UserAddress user={user} hidePhone={true} default={address} onChange={(newAddress: IAddress) => {
                                setAddress(newAddress);
                        }
                    }/>
                </Step>
                <Step>
                    <div className={styles.stepTitle}>Schedule</div>
                    <p>When will your course/class take place?</p>
                    <CourseSchedule
                        sessions={sessions}
                        timezone={props.course?.timezone}
                        courseType={selectedCourseType.id}
                        entryLimit={entryLimit}
                        waitlistSize={courseWaitlistSize}
                        onLimitsChange={(limit, size) => {
                            setEntryLimit(limit);
                            setCourseWaitlistSize(size);
                        }}
                        onChange={(newSessions) => {
                            setSessions(newSessions);
                        }}
                    />
                </Step>
                <Step>
                    <div className={styles.stepTitle}>Registration Confirmation Message</div>
                    <p>This message will be included in the email sent to participants when they register for this course. If left blank, a default message will be used.</p>
                    <Editor
                                apiKey={TINYMCE_API_KEY}
                                onInit={(evt, editor) => regMsgRef.current = editor}
                                init={{ ...TINYMCE_INIT, setup: TINYMCE_SETUP }}
                        initialValue={registrationMessage}
                    />
                </Step>
                <Step>
                    <div className={styles.inner}>
                        <div className={styles.paymentStructure}>
                            <div className={styles.titleInner}>Payment Structure</div>
                            <div className={styles.descInner}>Is your course pricing per session, or total for the course?</div>
                            <div className={styles.options}>
                                <div className={styles.option}>
                                    <Checkbox
                                        className={styles.struct}
                                        id="total_course"
                                        label="Total Course"
                                        value={totalCourse}
                                        disabled={true}
                                        onChange={() => {
                                            setTotalCourse(true);
                                            setPerSession(false);
                                        }}
                                    />
                                </div>
                                <div className={styles.option}>
                                    <Checkbox
                                        className={styles.struct}
                                        id="per_session"
                                        label="Per Session"
                                        value={perSession}
                                        disabled={true}
                                        onChange={() => {
                                            setTotalCourse(false);
                                            setPerSession(true);
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                        {/*<div className={styles.row}>
                            <Input required label="Waitlist Size" type="number" placeholder="What is the total number of waitlist spots?" value={waitlistSize} onChange={(e) => setWaitlistSize(e.target.value)} />
                        </div>*/}
                        <div className={styles.titleInner}>Payment Details</div>
                        <div className={styles.descInner}>
                            {totalCourse && <>What is the total price of your course/class?</>}
                            {perSession && <>What is the price of an individual session in your course/class?</>}
                        </div>
                        <div className={styles.row}>
                            <div className={styles.col}>
                                <Input required label="Price" type="number" placeholder="Amount" value={price} onChange={(e) => setPrice(e.target.value)} />
                            </div>
                            <div className={styles.col}>
                                <Dropdown
                                    label={"Currency"}
                                    placeholder="Currency"
                                    options={currencyOptions}
                                    value={currency}
                                    onChange={(e: any, value: IOption) => setCurrency(value)}
                                />
                            </div>
                        </div>
                    </div>
                </Step>
                <Step>
                    <div className={styles.stepTitle}>Course Waiver</div>
                    <p>Optionally add a custom waiver that registrants must agree to before completing their registration. Leave blank if no waiver is required.</p>
                    <Editor
                        apiKey={TINYMCE_API_KEY}
                        onInit={(evt, editor) => waiverRef.current = editor}
                        init={{ ...TINYMCE_INIT, setup: TINYMCE_SETUP }}
                        initialValue={waiver}
                    />
                </Step>
                <Step>
                    <div className={styles.stepTitle}>Club Policies</div>
                    <p>Optionally add club policies that registrants must agree to before completing their registration. Leave blank if no policies are required.</p>
                    <Editor
                        apiKey={TINYMCE_API_KEY}
                        onInit={(evt, editor) => clubPoliciesRef.current = editor}
                        init={{ ...TINYMCE_INIT, setup: TINYMCE_SETUP }}
                        initialValue={clubPolicies}
                    />
                </Step>
                <Step>
                    <div className={styles.vaccineStep}>
                        <div className={styles.stepTitle}>Vaccine Requirements</div>
                        <p>Optionally require dogs to have specific vaccine certificates on file before they can enroll in this course.</p>

                        <div className={styles.vaccineToggleRow}>
                            <Checkbox
                                id="vaccineEnabled"
                                label="Require vaccine certificates for enrollment"
                                value={vaccineEnabled}
                                onChange={() => setVaccineEnabled(v => !v)}
                            />
                        </div>

                        {vaccineEnabled && (
                            <>
                                <div className={styles.vaccineSection}>
                                    <div className={styles.vaccineSectionTitle}>Required vaccine types</div>
                                    <div className={styles.vaccineTypeList}>
                                        {DOG_VACCINE_TYPES.filter(t => t.value !== "other").map(t => (
                                            <div key={t.value} className={styles.vaccineTypeRow}>
                                                <Checkbox
                                                    id={`vaccine-${t.value}`}
                                                    label={t.label}
                                                    value={vaccineTypes.includes(t.value)}
                                                    onChange={() => toggleVaccineType(t.value)}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className={styles.vaccineSection}>
                                    <div className={styles.vaccineSectionTitle}>Enforcement</div>
                                    <div className={styles.vaccineEnforcementRow}>
                                        <label className={styles.vaccineRadioLabel}>
                                            <input
                                                type="radio"
                                                name="enforcement"
                                                value="block"
                                                checked={vaccineEnforcement === "block"}
                                                onChange={() => setVaccineEnforcement("block")}
                                            />
                                            Block registration if records are missing or expired
                                        </label>
                                        <label className={styles.vaccineRadioLabel}>
                                            <input
                                                type="radio"
                                                name="enforcement"
                                                value="warn"
                                                checked={vaccineEnforcement === "warn"}
                                                onChange={() => setVaccineEnforcement("warn")}
                                            />
                                            Show a warning but allow registration
                                        </label>
                                    </div>
                                </div>

                                <div className={styles.vaccineSection}>
                                    <div className={styles.vaccineSectionTitle}>Note to registrants (optional)</div>
                                    <textarea
                                        className={styles.vaccineNotes}
                                        placeholder="e.g. Please bring physical copies of records to your first class."
                                        rows={3}
                                        maxLength={500}
                                        value={vaccineNotes}
                                        onChange={e => setVaccineNotes(e.target.value)}
                                    />
                                </div>
                            </>
                        )}
                    </div>
                </Step>
                <Step>
                    <div className={styles.stepTitle}>Course Visibility</div>
                    <p>Control when your course becomes visible to the public and when entries open.</p>
                    <p>Course Status</p>
                    <Dropdown
                        value={courseVisibility}
                        onChange={(e: any, value: IOption) => { setCourseVisibility(value); }}
                        icon={dateIcon}
                        label="Visibility"
                        options={[
                            { value: "Draft", id: "Draft" },
                            { value: "Published", id: "Published" }
                        ]}
                        placeholder="Choose course visibility"
                    />
                    <p style={{ marginTop: "10px", fontSize: "14px", color: "#666" }}>
                        <strong style={{ fontWeight: "700" }}>Draft:</strong> Course is not visible to others - only you can see it.<br />
                        <strong style={{ fontWeight: "700" }}>Published:</strong> Course is visible to others on your club page and in search results.
                    </p>
                    {courseVisibility.value === "Published" ? (
                        <>
                            <p style={{ marginTop: "10px" }}>When do you want entries to open to the public?</p>
                            <Dropdown
                                value={publishOption}
                                onChange={(e: any, value: IOption) => { setPublishOption(value); }}
                                icon={dateIcon}
                                label="Entry Open Date"
                                options={[
                                    { value: "Immediately", id: "Immediately" },
                                    { value: "On a Specific Date", id: "On a Specific Date" }
                                ]}
                                placeholder="Choose a publishing option"
                            />
                            {publishOption.value === "On a Specific Date" && (
                                <DatePick
                                    showTimeSelect={true}
                                    label="Publish Date"
                                    onChange={setPublishDate}
                                    value={publishDate}
                                    rightText={courseTimezoneLabel}
                                />
                            )}
                        </>
                    ) : <></>}
                </Step>
            </StepForm>
            </div>
        </div>
    );
};

export default CreateCourse;